import { FC, ChangeEvent, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import Image from '@corratech/pylot-image'
import Link from 'next/link'
import { Tooltip } from '@corratech/pylot-ui'
import { usePrice } from '@corratech/pylot-price'
import {
    Maybe,
    WishlistItemInterface,
    ConfigurableWishlistItem
} from '@pylot-data/pylotschema'
import { WishlistManager } from '@corratech/pylot-wishlist/src/Hooks/WishlistActionTypes'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import s from '@pagestyles/Wishlist.module.scss'
import { getPDPUrl } from '@lib/getPDPUrl'
export type Props = {
    item: Maybe<ConfigurableWishlistItem & WishlistItemInterface>
    wishlistManager: WishlistManager
}

const urlSuffix = '.html'

export const WishlistProductGrid: FC<Props> = ({ item, wishlistManager }) => {
    const { t } = useTranslation(['wishlist'])
    const { wishlistState, addSelected, removeSelected } = wishlistManager
    const config = useStoreConfig()
    const { subtotal, total } = usePrice(item?.product?.price_range!)

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            addSelected([item?.id!])
        } else {
            removeSelected(item?.id!)
        }
    }

    const toolTipContent = useMemo(() => {
        const configurableOption = item?.configurable_options

        if (!configurableOption?.length) return null

        return (
            <Tooltip heading={t('wishlist|See Details')} direction="right">
                {configurableOption?.map((option, key) => (
                    <div className="text-left mb-1" key={key}>
                        <span className="font-bold">
                            {option?.option_label} :{' '}
                        </span>
                        <span> {option?.value_label}</span>
                    </div>
                ))}
            </Tooltip>
        )
    }, [item?.configurable_options, t])

    return (
        <div className="product-item-block">
            <div className="product-image-block">
                <Link href={getPDPUrl(item?.product?.url_key!)}>
                    <div className={s['wishlist-product-img-link']}>
                        <Image
                            src={String(item?.product?.small_image?.url)}
                            alt={String(item?.product?.name)}
                            width={320}
                            height={320}
                            className="product-img"
                        />
                    </div>
                </Link>
                {total && (
                    <div
                        className="product-price wishlist-product-price"
                        tabIndex={0}
                        role="button"
                    >
                        <span>{total}</span>
                        {subtotal !== total ? (
                            <span className="line-through text-t-3 pl-2">
                                {subtotal}
                            </span>
                        ) : null}
                    </div>
                )}
                <div className={s['product-select-action']}>
                    {config.base.wishlist.multiple_wishlists && (
                        <div>
                            <label
                                className="sr-only"
                                htmlFor={`wishlist-item${item?.id}`}
                            >
                                {item?.product?.name}
                            </label>
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                id={`wishlist-item${item?.id}`}
                                onChange={handleChange}
                                checked={wishlistState.selected.includes(
                                    String(item?.id)
                                )}
                            />
                        </div>
                    )}
                    <Link href={getPDPUrl(item?.product?.url_key!)}>
                        <h2 className={s['product-name']}>
                            {item?.product?.name}
                        </h2>
                    </Link>
                </div>
                {toolTipContent}
            </div>
        </div>
    )
}
